import { Box, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo } from 'react';
import rehypeHighlight from 'rehype-highlight';
import { MarkdownEditor } from './components/MarkdownEditor';
import MarkdownPreview from './components/MarkdownPreview';
import './config/highlightLanguages';
import { useMarkdownHistory } from './hooks/useMarkdownHistory';
import { createMarkdownStyles } from './styles';
import { CustomBr, CustomLink } from './components/Components';

interface DescriptionSectionProps {
    isEditing: boolean;
    editedDescription: string;
    setEditedDescription: (value: string) => void;
    updateDescription: (description: string) => void;
    description?: string;
}

interface MarkdownInputProps {
    checked?: boolean;
    index?: number;
}

export const DescriptionSection = ({
    isEditing,
    editedDescription,
    setEditedDescription,
    updateDescription,
    description
}: DescriptionSectionProps) => {
    const theme = useTheme();
    const { undo, redo, handleChange } = useMarkdownHistory({
        isEditing,
        editedDescription,
        setEditedDescription
    });
    const markdownStyles = useMemo(() => createMarkdownStyles(theme), [theme]);

    const handleCheckboxChange = (description: string, index: number) => {
        const lines = description.split('\n');
        const checkboxRegex = /(\s*-\s*\[)([ x])(\].*)$/;

        let checkboxCount = 0;
        const lineIndex = lines.findIndex(line => {
            if (checkboxRegex.test(line)) {
                if (checkboxCount === index) {
                    return true;
                }
                checkboxCount++;
            }
            return false;
        });

        if (lineIndex !== -1) {
            lines[lineIndex] = lines[lineIndex].replace(
                checkboxRegex,
                (_, start, check, end) => `${start}${check === ' ' ? 'x' : ' '}${end}`
            );
            const updatedDescription = lines.join('\n');
            updateDescription(updatedDescription);
        }
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isEditing) return;
            const ctrlPressed = e.ctrlKey || e.metaKey;
            const shiftPressed = e.shiftKey;
            const zPressed = e.key.toLowerCase() === 'z';

            if (zPressed && ctrlPressed && shiftPressed) {
                e.preventDefault();
                e.stopPropagation();
                redo();
            } else if (zPressed && ctrlPressed) {
                e.preventDefault();
                e.stopPropagation();
                undo();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [isEditing, undo, redo]);

    return (
        <Box sx={{ mb: 3 }}>
            <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                Description
            </Typography>
            {isEditing ? (
                <MarkdownEditor
                    value={editedDescription}
                    onChange={handleChange}
                    undo={undo}
                    redo={redo}
                    customStyles={markdownStyles}
                    previewOptions={{
                        rehypePlugins: [[rehypeHighlight, { detect: true, ignoreMissing: true }]],
                        components: {
                            input: ({ checked, index }: MarkdownInputProps) => (
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => null}
                                />
                            ),
                            br: () => <CustomBr />,
                            a: (props) => <CustomLink {...props} />,
                        },
                    }}
                />
            ) : (
                <MarkdownPreview
                    description={editedDescription || description}
                    markdownStyles={markdownStyles}
                    rehypePlugins={[[rehypeHighlight, { detect: true, ignoreMissing: true }]]}
                    onCheckboxChange={handleCheckboxChange}
                />
            )}
        </Box>
    );
};